<template>
    <div>
        <div
            v-if="crossword && hasVocabularyTerms(crossword.vocab_data)"
            class="position-relative"
        >
            <div v-show="!loading" class="mb-2">
                <template v-if="!term_description_pairs.length">
                    <b-alert variant="warning" class="mb-0" show>
                        <h6 class="font-weight-bold">
                            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                            0 Vocabulary Terms linked
                        </h6>
                        <div>Please link vocabulary terms to generate a crossword puzzle</div>
                    </b-alert>
                </template>
                <template v-else-if="term_description_pairs.length < 3">
                    <b-alert variant="warning" class="mb-0" show>
                        <h6 class="font-weight-bold">
                            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                            {{ term_description_pairs.length }}
                            Vocabulary Term{{ term_description_pairs.length > 1 ? 's' : '' }} linked
                        </h6>
                        <div>Crossword/Misspilled require 3 vocabulary words to be displayed on SWO</div>
                    </b-alert>
                </template>
                <template v-if="crossword_invalid_terms.length > 0">
                    <b-alert variant="warning" class="mb-0" show>
                        <h6 class="font-weight-bold">
                            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                            Crossword contains terms not found within the Week
                        </h6>
                        <ul class="mb-0">
                            <li v-for="crossword_invalid_term in crossword_invalid_terms" :key="crossword_invalid_term.term_id">
                                {{ crossword_invalid_term.answer }} ({{crossword_invalid_term.term_id}})
                            </li>
                        </ul>
                    </b-alert>
                </template>
            </div>

            <div
                v-show="term_description_pairs && term_description_pairs.length"
            >
                <div class="d-flex mb-3">
                    <div class="mb-lg-0 mb-3 text-nowrap ml-lg-auto">
                        <b-btn
                            size="sm"
                            variant="secondary"
                            class="mr-1"
                            :disabled="reweaving || !can_update_week || disabled"
                            @click.prevent="reweaveCrossword"
                        >
                            <span v-if="reweaving">
                                <b-spinner label="Spinning" small></b-spinner>
                            </span>
                            <span v-else>
                                Reweave
                            </span>
                        </b-btn>
                        <b-dropdown
                            size="sm"
                            variant="outline-secondary"
                            right
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['fad', 'cog']" />
                            </template>

                            <b-dropdown-item
                                :to="{name: 'PreviewBlankCrossword', params: {publication_id: publication_id, unit_id: unit_id, crossword_seed: crossword.crossword_data.seed}}"
                                target="_blank">
                                <font-awesome-icon :icon="['fad', 'file-export']" class="text-secondary" fixed-width />
                                Export Blank Crossword
                            </b-dropdown-item>

                            <b-dropdown-item
                                :to="{name: 'PreviewFilledCrossword', params: {publication_id: publication_id, unit_id: unit_id, crossword_seed: crossword.crossword_data.seed}}"
                                target="_blank"
                            >
                                <font-awesome-icon :icon="['fad', 'file-export']" class="text-secondary" fixed-width />
                                Export Filled Crossword
                            </b-dropdown-item>

                            <b-dropdown-item
                                :disabled="downloading_svg || downloading_blank_svg || downloading_clues_text || reweaving || loading"
                                @click.prevent="downloadCrosswordSvg(false)"
                            >
                                <font-awesome-icon :icon="['fad', 'download']" class="text-secondary" fixed-width />
                                <span v-if="downloading_svg">Downloading SVG...</span>
                                <span v-else>Download SVG</span>
                            </b-dropdown-item>

                            <b-dropdown-item
                                :disabled="downloading_svg || downloading_blank_svg || downloading_clues_text || reweaving || loading"
                                @click.prevent="downloadCrosswordSvg(true)"
                            >
                                <font-awesome-icon :icon="['fad', 'download']" class="text-secondary" fixed-width />
                                <span v-if="downloading_blank_svg">Downloading SVG...</span>
                                <span v-else>Download Blank SVG</span>
                            </b-dropdown-item>

                            <b-dropdown-item
                                :disabled="downloading_clues_text || reweaving || loading || !my_crossword"
                                @click.prevent="downloadCluesText"
                            >
                                <font-awesome-icon :icon="['fad', 'download']" class="text-secondary" fixed-width />
                                <span v-if="downloading_clues_text">Downloading...</span>
                                <span v-else>Download Clues Text</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
                <div v-if="reweaving_error">
                    <div class="text-danger">
                        There was an issue weaving the crossword. Please try again or try using different vocabulary
                        terms.
                    </div>
                </div>
                <div v-show="!reweaving_error" class="crossword-container">
                    <b-badge
                        v-if="!loading && crossword && crossword.crossword_data"
                        class="mb-2"
                        :variant="unsafe_crossword_size ? 'warning': 'info'"
                    >
                        <span
                            v-if="unsafe_crossword_size"
                            class="mr-1"
                            content="This board exceeds 15 characters in an axis, and will not fit when printed"
                            v-tippy
                        >
                            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                        </span>
                        Board Size:
                        {{ crossword.crossword_data.width === 0 ? 1 : crossword.crossword_data.width }}
                        x
                        {{ crossword.crossword_data.height === 0 ? 1 : crossword.crossword_data.height }}
                    </b-badge>
                    <div id="crossword"></div>
                </div>
            </div>

            <div class="mt-3">
                <h6>Vocabulary Terms</h6>

                <div v-if="!loading && crossword && crossword.vocab_data">
                    <b-list-group>
                        <b-list-group-item
                            v-for="(term, index) in crossword.vocab_data"
                            :key="index"
                        >
                            <div class="d-flex align-items-center">
                                <span
                                    v-if="term.term.replace(/[\s\-().]/g, '').length > 15" class="mr-2 text-warning"
                                    content="This word exceeds the 15 character max for Crossword and Misspilled."
                                    v-tippy
                                >
                                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                                </span>
                                <div>
                                    <b-form-checkbox
                                        v-model="term.linked_to_crossword"
                                        :checked="term.linked_to_crossword"
                                        switch
                                        @change="linkVocabularyTerm($event, term)"
                                        :disabled="
                                            !can_update_week
                                            || disabled
                                            || (
                                                !term.linked_to_crossword &&
                                                term.term.replace(/[\s\-().]/g, '').length > 15
                                            )
                                        "
                                    ></b-form-checkbox>
                                </div>
                                <div>
                                    <div>
                                        <strong>{{ term.term }}</strong>
                                    </div>
                                    {{ term.definition }}
                                </div>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </div>
                <div v-if="!loading && (!crossword || crossword && !crossword.vocab_data)">
                    <b-alert variant="danger" show>0 available vocabulary terms</b-alert>
                </div>
            </div>
        </div>
        <div
            v-else-if="
                !loading &&
                (!crossword || crossword && !hasVocabularyTerms(crossword.vocab_data))
            "
        >
            <b-alert variant="danger" class="mb-0" show>
                <h6 class="font-weight-bold">0 Vocabulary Terms linked to this week</h6>
                <div>
                    Please add vocabulary terms to articles in this week before trying to generate a crossword puzzle
                </div>
            </b-alert>
        </div>
    </div>
</template>

<style lang="scss">
@import '@/assets/css/crossword-old.scss';
</style>

<script>
import VueSticky from 'vue-sticky'
import {mapState, mapGetters} from 'vuex'
import crossword_puzzle from '@/common/crossword'
import moment from 'moment'
import {toSvg} from 'html-to-image'
import {downloadBlob} from '@/common/fileDownload'
import stripHtml from 'string-strip-html'

export default {
    data: () => ({
        term_description_pairs: [],
        selected_clue: {},
        my_crossword: null,
        seed: 1,
        reweaving: false,
        reweaving_error: false,
        saving_crossword: false,
        downloading_svg: false,
        downloading_blank_svg: false,
        downloading_clues_text: false,
        loading: false,
    }),
    props: [
        'publication_id',
        'unit_id',
        'publication',
        'unit',
        'disabled',
    ],
    directives: {
        'sticky': VueSticky,
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        ...mapGetters([
            'crosswordForUnit'
        ]),
        crossword() {
            return this.crosswordForUnit(this.unit_id)
        },
        can_update_week() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_week')
        },
        unsafe_crossword_size() {
            const hasCrosswordData = this.crossword && this.crossword.crossword_data
            return hasCrosswordData && (
                this.crossword.crossword_data.width > 15 || this.crossword.crossword_data.height > 15
            )
        },
        crossword_invalid_terms() {
            // Grab terms from the unit and terms within the clues,
            // then check to see if any clue terms are not within the unit terms.
            const vocabTerms = _.get(this.crossword, 'vocab_data')
            const clues = _.get(this.crossword, 'crossword_data.clues')
            if (!vocabTerms || !clues || !Array.isArray(clues)) {
                return false
            }
            const termIds = vocabTerms.map(term => term.vocabulary_term_id)
            return clues
                .map((clue) => ({ term_id: clue.term_id, answer: clue.Answer }))
                .filter((clue) => termIds.includes(clue.term_id) === false)
        },
    },
    watch: {
        crossword: {
            handler: function(val) {
                const clues = _.get(val, 'crossword_data.clues')
                if (!clues || !Array.isArray(clues) || clues.length === 0) {
                    // Reset the board if there is one
                    const crosswordNode = document.getElementById('crossword')
                    crosswordNode.innerHTML = ''
                    // Reset the terms if there are any
                    this.term_description_pairs = []
                }
            }
        }
    },
    methods: {
        linkVocabularyTerm(linked, term) {
            this.$set(term, 'linked_to_crossword', linked)

            this.generateCrossword()
        },
        async generateCrossword() {
            this.reweaving_error = false
            this.term_description_pairs = []
            let crossword_node = document.getElementById('crossword')
            let seed = null

            if (_.get(this.crossword, 'crossword_data.seed')) {
                seed = this.crossword.crossword_data.seed
            } else {
                seed = 1
            }

            _.forEach(this.crossword.vocab_data, term => {
                // Always exclude terms greater than 15 characters (excluding spaces, dots, dashes and parens)
                if (term.term && term.term.replace(/[\s\-().]/g, '').length > 15) {
                    term.linked_to_crossword = false
                }

                if (term.linked_to_crossword) {
                    this.term_description_pairs.push({
                        term: term.term,
                        term_id: term.vocabulary_term_id,
                        description: {
                            html: term.definition,
                            definition_id: term.definition_id
                        }
                    })
                }
            })

            // Do not generate an empty crossword puzzle
            if (this.term_description_pairs.length === 0) {
                await this.$store.dispatch('saveCrossword', {json: null, unit_id: this.unit_id})
                this.reweaving = false
                return
            }

            try {
                const updatedCrossword = new crossword_puzzle.Crossword({
                    node: crossword_node,
                    separateDescriptions: true,
                    subset: 18,
                    allow_special_characters: false,
                    seed: seed,
                    preComplete: true,
                    previewMode: false,
                    termDescriptionPairs: this.term_description_pairs
                })
                const crosswordJsonString = updatedCrossword.exportJSON()

                if (!crosswordJsonString) {
                    throw new Error('No crossword data found')
                }

                const crosswordJson = JSON.parse(crosswordJsonString)
                let readyToSave = true
                if (crosswordJson.width > 15 || crosswordJson.height > 15) {
                    const {isConfirmed} = await this.$swal({
                        html: 'This crossword exceeds 15 characters in an axis. Are you sure you want to update this crossword?',
                        showCancelButton: true,
                        confirmButtonText: 'Update Crossword',
                        cancelButtonText: 'Cancel',
                        reverseButtons: true
                    })
                    if (!isConfirmed) {
                        await this.$store.dispatch('getCrossword', {unit_id: this.unit_id})
                        await this.displayCrossword()
                        readyToSave = false
                    }
                }
                if (readyToSave) {
                    await this.$store.dispatch('saveCrossword', {json: crosswordJson, unit_id: this.unit_id})
                    this.reweaving = false
                    this.my_crossword = updatedCrossword
                }
            } catch (error) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to load crossword: ${error.message}`,
                    },
                    time: moment().toISOString(),
                })
                this.reweaving = false
                this.reweaving_error = true
            }
        },
        reweaveCrossword() {
            this.reweaving = true
            const seed = _.get(this.crossword, 'crossword_data.seed')

            if (seed !== undefined) {
                this.$store.dispatch('updateSeed', {unit_id: this.unit_id, seed: seed + 1})
            }

            this.generateCrossword()
        },
        downloadCluesText() {
            if (!this.my_crossword || !this.my_crossword.termsPlaced.length) {
                return
            }

            this.downloading_clues_text = true

            try {
                const lines = ['Descriptions', '']

                this.my_crossword.termsPlaced.forEach((term, index) => {
                    lines.push(`${index + 1}. ${stripHtml(term.description.html).result}`)
                })

                const text = lines.join('\n')
                const blob = new Blob([text], {type: 'text/plain;charset=utf-8'})
                const unitName = _.get(this.unit, 'title') || `unit-${this.unit_id}`
                const safeUnitName = unitName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')

                downloadBlob(blob, `${safeUnitName}-crossword-clues.txt`)
            } catch (error) {
                this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to download crossword clues: ${error.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.downloading_clues_text = false
            }
        },
        async downloadCrosswordSvg(blank = false) {
            const crosswordTable = document.querySelector('#crossword table')

            if (!crosswordTable) {
                return
            }

            const inputs = blank ? Array.from(crosswordTable.querySelectorAll('input')) : []
            const savedValues = blank ? inputs.map(input => input.value) : []

            if (blank) {
                inputs.forEach(input => {
                    input.value = ''
                })
                this.downloading_blank_svg = true
            } else {
                this.downloading_svg = true
            }

            try {
                const svgDataUrl = await toSvg(crosswordTable, {
                    backgroundColor: '#ffffff',
                })
                const downloadLink = document.createElement('a')
                const unitName = _.get(this.unit, 'title') || `unit-${this.unit_id}`
                const safeUnitName = unitName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')
                const suffix = blank ? '-crossword-blank' : '-crossword'

                downloadLink.href = svgDataUrl
                downloadLink.download = `${safeUnitName}${suffix}.svg`
                document.body.appendChild(downloadLink)
                downloadLink.click()
                document.body.removeChild(downloadLink)
            } catch (error) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to download crossword SVG: ${error.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                if (blank) {
                    inputs.forEach((input, index) => {
                        input.value = savedValues[index]
                    })
                    this.downloading_blank_svg = false
                } else {
                    this.downloading_svg = false
                }
            }
        },
        hasVocabularyTerms(vocab_data) {
            let hasVocabularyTerms = false

            if (_.some(vocab_data)) {
                hasVocabularyTerms = true
            }

            return hasVocabularyTerms
        },
        async displayCrossword() {
            this.term_description_pairs = []
            let crossword_node = document.getElementById('crossword')

            let args = {
                node: crossword_node,
                separateDescriptions: true,
                subset: 18,
                allow_special_characters: false,
                seed: 1,
                preComplete: true,
                previewMode: false,
                termDescriptionPairs: this.term_description_pairs
            }

            _.forEach(this.crossword.vocab_data, term => {
                if (term.linked_to_crossword) {
                    this.term_description_pairs.push({
                        term: term.term,
                        term_id: term.vocabulary_term_id,
                        description: {
                            html: term.definition,
                            definition_id: term.definition_id
                        }
                    })
                }
            })
            if (this.term_description_pairs.length > 0) {
                try {
                    this.my_crossword = new crossword_puzzle.Crossword(args)
                    this.my_crossword.importJSON(this.crossword.crossword_data)
                    crossword_node.innerHTML = ''
                    let node = this.my_crossword.grid2Html()
                    crossword_node.appendChild(node)
                    let node2 = this.my_crossword.descriptions2Html()
                    node.appendChild(node2)
                } catch (e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to display crossword: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                }
            }
        }
    },
    async mounted() {
        this.loading = true
        await this.$store.dispatch('getCrossword', {unit_id: this.unit_id})
        await this.displayCrossword()
        this.loading = false
    }
}
</script>
